/**
 * Helper class for dynamic component loading and initialization.
 * Provides utilities to handle component paths and dynamically import components
 * based on data attributes in the DOM.
 */
class ComponentHelper {
    /**
     * Prepare parts of string like type/component to be used for dynamic import.
     * Converts a raw component path format (e.g., "molecules/Button") into a
     * properly formatted path for dynamic imports with proper capitalization.
     *
     * @param {string} rawValue - The raw component path in format "type/name"
     * @returns {string|null} - Formatted component path or null if input is invalid
     */
    static prepareComponentPath(rawValue) {
        if (rawValue.indexOf('/') === -1) {
            return null;
        }
        const typeName = rawValue.split('/')[0].toLowerCase();

        let componentName = rawValue.split('/')[1];
        componentName = componentName.substr(0, 1).toUpperCase() + componentName.substr(1);

        return `${typeName}/${componentName}`;
    }

    /**
     * Iterates over all DOM nodes with attribute "data-component" and uses its value to dynamically
     * import the corresponding component. After successful import, initializes the component.
     *
     * @param {HTMLElement|Document} parentNode - The parent node to search within (defaults to document)
     * @returns {void}
     */
    static initializeNewDataComponents(parentNode = document) {
        for (const node of parentNode.querySelectorAll('[data-component]')) {
            const componentPath = ComponentHelper.prepareComponentPath(node.getAttribute('data-component'));

            if (typeof componentPath !== 'string') {
                continue; // eslint-disable-line no-continue
            }

            import(`../../../components/${componentPath}`).then((Component) => {
                const comp = new Component.default(node); // eslint-disable-line new-cap
                comp.init();
            }).catch((e) => console.error(e.message)); // eslint-disable-line no-console
        }
    }
}

export default ComponentHelper;
